import type React from "react"
import { createContext, useContext, useState, useRef } from "react"
import { Plus } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

export interface KanbanColumnDef<TStatus = string> {
  status: TStatus
  label: string
  color?: string
}

export interface KanbanItem<TStatus = string> {
  id: string
  status: TStatus
  [key: string]: any // Allow any additional properties
}

interface KanbanBoardProps<TItem extends KanbanItem<TStatus>, TStatus = string> {
  items?: TItem[]
  columns: KanbanColumnDef<TStatus>[]
  onItemMove?: (itemId: string, newStatus: TStatus, oldStatus: TStatus, item?: TItem) => Promise<void>
  renderCard: (item: TItem, isLoading: boolean) => React.ReactNode
  getItemId?: (item: TItem) => string
  getItemStatus?: (item: TItem) => TStatus
  onCardClick?: (itemId: string, item: TItem) => void
  onCardHover?: (itemId: string, item: TItem) => void
  onAddItem?: (status: TStatus) => void
  /**
   * Composition mode: when provided, the caller renders its own `KanbanColumn`s
   * (each owning its data) inside the board's drag context + scroll container,
   * and the `items` prop is ignored.
   */
  children?: React.ReactNode
}

/**
 * Drag state + handlers shared between the board and its columns via context.
 * Module-private and intentionally untyped over the item/status generics —
 * `KanbanColumn` re-narrows at its own prop boundary.
 */
interface KanbanContextValue {
  draggedItem: string | null
  dragOverColumn: any
  dragOverIndex: number | null
  loadingItems: Set<string>
  handleMouseDown: (e: React.MouseEvent, itemId: string) => void
  handleDragStart: (itemId: string, item: any, status: any) => void
  handleCardClick: (e: React.MouseEvent, itemId: string, item: any) => void
  handleCardDragOver: (e: React.DragEvent, status: any, index: number) => void
  handleColumnDragOver: (e: React.DragEvent, status: any, isEmpty: boolean) => void
  handleDrop: (status: any) => void
  handleDragEnd: () => void
  renderCard: (item: any, isLoading: boolean) => React.ReactNode
  getItemId: (item: any) => string
  onCardHover?: (itemId: string, item: any) => void
  onAddItem?: (status: any) => void
}

const KanbanContext = createContext<KanbanContextValue | null>(null)

function useKanbanContext(): KanbanContextValue {
  const context = useContext(KanbanContext)
  if (!context) {
    throw new Error("KanbanColumn must be rendered inside a KanbanBoard")
  }
  return context
}

// Auto-scroll configuration
const SCROLL_EDGE_THRESHOLD = 100 // pixels from edge to trigger scroll
const SCROLL_SPEED = 15 // pixels per frame

export function KanbanBoard<TItem extends KanbanItem<TStatus>, TStatus = string>({
  items: initialItems = [],
  columns,
  onItemMove,
  renderCard,
  getItemId = (item) => item.id,
  getItemStatus = (item) => item.status,
  onCardClick,
  onCardHover,
  onAddItem,
  children,
}: KanbanBoardProps<TItem, TStatus>) {
  const [draggedItem, setDraggedItem] = useState<string | null>(null)
  const [dragOverColumn, setDragOverColumn] = useState<TStatus | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set())
  const mouseDownRef = useRef<{ x: number; y: number; itemId: string | null }>({ x: 0, y: 0, itemId: null })
  // Full dragged item + source column captured at drag start, so drop doesn't
  // need to find the item in a flat array (columns may own their own data).
  const draggedRef = useRef<{ itemId: string; item: TItem; status: TStatus } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollAnimationRef = useRef<number | null>(null)

  const handleMouseDown = (e: React.MouseEvent, itemId: string) => {
    mouseDownRef.current = {
      x: e.clientX,
      y: e.clientY,
      itemId,
    }
  }

  const handleDragStart = (itemId: string, item: TItem, status: TStatus) => {
    draggedRef.current = { itemId, item, status }
    setDraggedItem(itemId)
  }

  const handleCardClick = (e: React.MouseEvent, itemId: string, item: TItem) => {
    // Check if mouse moved significantly (more than 5px) - indicates a drag
    if (mouseDownRef.current.itemId === itemId) {
      const distance = Math.sqrt(
        Math.pow(e.clientX - mouseDownRef.current.x, 2) +
        Math.pow(e.clientY - mouseDownRef.current.y, 2)
      )

      // Only trigger click if mouse didn't move much (wasn't a drag)
      if (distance < 5 && onCardClick) {
        onCardClick(itemId, item)
      }
    }
  }

  const handleCardDragOver = (e: React.DragEvent, status: TStatus, index: number) => {
    e.preventDefault()
    e.stopPropagation()

    if (dragOverColumn !== status || dragOverIndex !== index) {
      setDragOverColumn(status)
      setDragOverIndex(index)
    }
  }

  const handleColumnDragOver = (e: React.DragEvent, status: TStatus, isEmpty: boolean) => {
    e.preventDefault()

    if (isEmpty) {
      setDragOverColumn(status)
      setDragOverIndex(null)
    }
  }

  const handleDrop = async (status: TStatus) => {
    const dragged = draggedRef.current
    if (!dragged) return

    const { itemId, item, status: oldStatus } = dragged
    draggedRef.current = null

    setDraggedItem(null)
    setDragOverColumn(null)
    setDragOverIndex(null)

    if (oldStatus === status) return

    if (onItemMove) {
      setLoadingItems((prev) => new Set(prev).add(itemId))
      try {
        await onItemMove(itemId, status, oldStatus, item)
      } catch (error) {
        console.error("Failed to update item:", error)
      } finally {
        setLoadingItems((prev) => {
          const newSet = new Set(prev)
          newSet.delete(itemId)
          return newSet
        })
      }
    }
  }

  const handleDragEnd = () => {
    setDragOverColumn(null)
    setDragOverIndex(null)
    setDraggedItem(null)
    draggedRef.current = null
    mouseDownRef.current = { x: 0, y: 0, itemId: null }
    // Cleanup scroll animation
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current)
      scrollAnimationRef.current = null
    }
  }

  // Auto-scroll when dragging near container edges
  const scrollContainer = (delta: number) => {
    const container = containerRef.current
    if (!container) return

    container.scrollLeft += delta

    // Continue scrolling while dragging
    if (draggedItem) {
      scrollAnimationRef.current = requestAnimationFrame(() => scrollContainer(delta))
    }
  }

  const handleAutoScroll = (clientX: number) => {
    const container = containerRef.current
    if (!container || !draggedItem) return

    const rect = container.getBoundingClientRect()
    const distanceFromLeft = clientX - rect.left
    const distanceFromRight = rect.right - clientX

    // Cancel any existing animation
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current)
      scrollAnimationRef.current = null
    }

    // Scroll left
    if (distanceFromLeft < SCROLL_EDGE_THRESHOLD) {
      const intensity = 1 - distanceFromLeft / SCROLL_EDGE_THRESHOLD
      scrollContainer(-SCROLL_SPEED * intensity)
    }
    // Scroll right
    else if (distanceFromRight < SCROLL_EDGE_THRESHOLD) {
      const intensity = 1 - distanceFromRight / SCROLL_EDGE_THRESHOLD
      scrollContainer(SCROLL_SPEED * intensity)
    }
  }

  const handleContainerDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    handleAutoScroll(e.clientX)
  }

  const contextValue: KanbanContextValue = {
    draggedItem,
    dragOverColumn,
    dragOverIndex,
    loadingItems,
    handleMouseDown,
    handleDragStart,
    handleCardClick,
    handleCardDragOver,
    handleColumnDragOver,
    handleDrop,
    handleDragEnd,
    renderCard,
    getItemId,
    onCardHover,
    onAddItem,
  }

  return (
    <KanbanContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        className="h-full flex gap-4 overflow-x-scroll overflow-y-hidden pb-4 scrollbar-always-visible"
        onDragOver={handleContainerDragOver}
      >
        {children ??
          columns.map((column) => (
            <KanbanColumn<TItem, TStatus>
              key={String(column.status)}
              column={column}
              items={initialItems.filter((item) => getItemStatus(item) === column.status)}
            />
          ))}
      </div>
    </KanbanContext.Provider>
  )
}

interface KanbanColumnProps<TItem extends KanbanItem<TStatus>, TStatus = string> {
  column: KanbanColumnDef<TStatus>
  items: TItem[]
  /** Server-side total for the column; shows "{loaded} of {total}" when it exceeds the loaded rows. */
  totalCount?: number
  /**
   * Rows loaded from the server, for the "{loaded} of {total}" badge. Defaults
   * to `items.length` — pass explicitly when `items` has been narrowed by a
   * client-side filter, so the badge reflects fetch progress rather than the
   * filtered subset actually rendered.
   */
  loadedCount?: number
  /** When false, renders skeleton cards instead of the item list / empty state. Defaults to true. */
  hasLoaded?: boolean
  /** Rendered after the card list, inside the column's scrollable area (e.g. a "Load more" button). */
  footer?: React.ReactNode
  /** Overrides the default empty-column body. */
  placeholder?: React.ReactNode
}

export function KanbanColumn<TItem extends KanbanItem<TStatus>, TStatus = string>({
  column,
  items,
  totalCount,
  loadedCount = items.length,
  hasLoaded = true,
  footer,
  placeholder,
}: KanbanColumnProps<TItem, TStatus>): React.ReactNode {
  const {
    draggedItem,
    dragOverColumn,
    dragOverIndex,
    loadingItems,
    handleMouseDown,
    handleDragStart,
    handleCardClick,
    handleCardDragOver,
    handleColumnDragOver,
    handleDrop,
    handleDragEnd,
    renderCard,
    getItemId,
    onCardHover,
    onAddItem,
  } = useKanbanContext()

  const badgeLabel =
    totalCount !== undefined && loadedCount > 0 && totalCount > loadedCount
      ? `${loadedCount} of ${totalCount}`
      : totalCount ?? loadedCount

  return (
    <div
      className="flex min-w-[320px] flex-1 flex-col min-h-0"
      onDragOver={(e) => handleColumnDragOver(e, column.status, items.length === 0)}
      onDrop={() => handleDrop(column.status)}
    >
      <div className="mb-4 flex items-center gap-2">
        {column.color && <div className={cn("h-2 w-2 rounded-full", column.color)} />}
        <h2 className="text-lg font-semibold text-foreground">{column.label}</h2>
        <Badge variant="secondary" className="ml-auto">
          {badgeLabel}
        </Badge>
      </div>

      <div
        className="scroll-fade-y flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto rounded-lg bg-muted/50 p-3"
      >
        {!hasLoaded ? (
          <>
            <Skeleton className="h-[120px] shrink-0 rounded-lg" />
            <Skeleton className="h-[120px] shrink-0 rounded-lg" />
            <Skeleton className="h-[120px] shrink-0 rounded-lg" />
          </>
        ) : items.length === 0 ? (
          <>
            {draggedItem && dragOverColumn === column.status && dragOverIndex === null ? (
              <div className="h-[120px] transition-all duration-200" />
            ) : placeholder !== undefined ? (
              placeholder
            ) : (
              <div
                className={cn(
                  "flex h-32 items-center justify-center rounded-lg border-2 border-dashed border-border group",
                  onAddItem && "cursor-pointer hover:border-primary hover:bg-muted/80 transition-colors"
                )}
                onClick={() => onAddItem?.(column.status)}
              >
                {onAddItem ? (
                  <div className="flex flex-col items-center gap-1 text-muted-foreground group-hover:text-primary transition-colors">
                    <Plus className="h-5 w-5" />
                    <p className="text-sm">Add Lead</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No items</p>
                )}
              </div>
            )}
          </>
        ) : (
          items.map((item, index) => {
            const itemId = getItemId(item)
            const isLoading = loadingItems.has(itemId)

            return (
              <div key={itemId}>
                {draggedItem &&
                  draggedItem !== itemId &&
                  dragOverColumn === column.status &&
                  dragOverIndex === index && <div className="mb-3 h-[120px] transition-all duration-200" />}
                <Card
                  draggable
                  onMouseDown={(e) => handleMouseDown(e, itemId)}
                  onDragStart={() => handleDragStart(itemId, item, column.status)}
                  onDragOver={(e) => handleCardDragOver(e, column.status, index)}
                  onDragEnd={handleDragEnd}
                  onClick={(e) => handleCardClick(e, itemId, item)}
                  onMouseEnter={() => onCardHover?.(itemId, item)}
                  className={cn(
                    "cursor-grab active:cursor-grabbing transition-opacity duration-200 py-0",
                    draggedItem === itemId && "opacity-50",
                    isLoading && "opacity-50 pointer-events-none",
                  )}
                >
                  {renderCard(item, isLoading)}
                </Card>
              </div>
            )
          })
        )}
        {hasLoaded && footer}
      </div>
    </div>
  )
}
